/**
 * Locale translations: fr (French).
 * Keys and structure are shared across all locale files.
 */
export const fr = {
  // Common
  common: {
    loading: "Chargement...",
    error: "Erreur",
    sort: "Trier",
    sortAsc: "Tri croissant",
    sortDesc: "Tri décroissant",
    save: "Enregistrer",
    cancel: "Annuler",
    delete: "Supprimer",
    remove: "Retirer",
    edit: "Modifier",
    view: 'Consulter',
    copy: "Copier",
    create: "Créer",
    update: "Mettre à jour",
    search: "Rechercher",
    actions: "Actions",
    code: "Code",
    all: "Tous",
    clear: "Effacer",
    filter: "Filtrer",
    yes: "Oui",
    no: "Non",
    confirm: "Confirmer",
    close: "Fermer",
    select: "Sélectionner",
    active: "Actif",
    inactive: "Inactif",
    success: "Succès",
    updated: "Mis à jour",
    created: "Créé",
    deleted: "Supprimé",
    confirmDelete: "Êtes-vous sûr de vouloir supprimer ?",
    errorOccurred: "Une erreur est survenue",
    noData: "Aucune donnée",
    status: "Statut",
    show: "Afficher",
    hide: "Masquer",
    searchPlaceholder: "Rechercher...",
    allStatuses: "Tous les statuts",
    clearFilters: "Effacer les filtres",
    newRecord: "Nouvel enregistrement",
    noPermission: "Pas de permission",
    statusDistribution: "Distribution des statuts",
    rows: "lignes",
    firstPage: "Première page",
    previousPage: "Page précédente",
    nextPage: "Page suivante",
    lastPage: "Dernière page",
    page: "Page",
    optional: "Optionnel",
    saving: "Enregistrement...",
    deleteConfirmTitle: "Confirmer la suppression",
    deleteWarning: "Cette action ne peut pas être annulée !",
    autoTranslate: "Traduction automatique",
    autoTranslateSuccessCount: "{count} éléments traduits automatiquement.",
    autoTranslateNoSource: "Traduction source introuvable.",
    enterTargetLanguage: "Veuillez choisir la langue cible.",
    sourceLanguageLabel: "Langue source (votre interface)",
    targetLanguage: "Langue cible",
    sourceAndTargetMustDiffer: "Les langues source et cible doivent être différentes.",
    context: "Contexte",
    key: "Clé",
    value: "Valeur",
    locale: "Langue",
    auto: "Auto",
    manual: "Manuel",
    manageLocales: "Gérer les langues",
    activeLocalesDescription: "Choisissez les langues actives. Enregistré uniquement comme paramètre.",
    updateUntranslatedFields: "Synchroniser les nouveaux champs",
    syncEntriesResult: "{count} nouvelles entrées de traduction synchronisées.",
    syncDeletedResult: "{count} entrées obsolètes supprimées.",
    selectSourceLanguageForSync: "Dans quelle langue est le contenu ?",
    sourceLanguage: "Langue source",
    originalShort: "Orig.",
    languageNames: { en: "Anglais", tr: "Turc", de: "Allemand", fr: "Français", es: "Espagnol", ar: "Arabe", it: "Italien", ja: "Japonais", zh: "Chinois", ru: "Russe" } as Record<string, string>,
    perPage: "Par page",
    darkMode: "Mode sombre",
    lightMode: "Mode clair",
    copyright: "© {year} QubePro. Tous droits réservés.",
    translationRates: "Taux de traduction",
    profileMenu: "Menu profil",
    manageYourAccount: "Gérer le compte",
    adminConsole: "Console d'administration",
    serverConfiguring: "Configuration du serveur en cours, veuillez patienter...",
    productName: "Gestion",
  },

  portal: {
    searchPlaceholder: "Rechercher des applications...",
    noAppsFound: "Aucune application trouvée.",
    quickLinks: "Liens rapides",
    console: "Console administrateur",
    lastLogin: "Dernière connexion",
    apps: {
      core: { name: "Gestion", description: "Gérez les entreprises, organisations, utilisateurs, rôles et permissions dans une plateforme centralisée." },
      bi: { name: "Analytique", description: "Créez des rapports et tableaux de bord à partir de vos données. Transformez les données en informations." },
      bpm: { name: "Processus", description: "Démarrez, suivez et terminez les demandes internes, approbations et tâches numériquement." },
      support: { name: "Support", description: "Gérez les demandes clients et les tickets. Suivez et résolvez les problèmes au même endroit." },
      agent: { name: "Automatisation", description: "Automatisez les tâches routinières avec des bots et des agents. S'intègre à vos outils." },
      ai: { name: "IA et Prévisions", description: "Utilisez des modèles d'IA pour l'analyse de données et les prévisions. Améliorez la planification." },
      erp: { name: "Suite Business", description: "Modules pour la finance, les ventes, les stocks et les opérations. Gérez votre entreprise sur une plateforme." },
      pms: { name: "Projets", description: "Planifiez des projets, assignez des tâches, suivez les progrès et collaborez en équipe." },
      crm: { name: "Clients", description: "Gérez les contacts, prospects, offres et pipelines de vente." },
    },
    menus: {
      core_dashboard: "Tableau de bord",
      core_companies: "Entreprises",
      core_organizations: "Organisations",
      core_roles: "Rôles",
      core_users: "Utilisateurs",
      core_groups: "Groupes",
      core_emails: "Comptes e-mail",
      core_data_sources: "Sources de données",
      core_settings_company: "Paramètres entreprise",
      core_settings_user: "Paramètres utilisateur",
      core_translations: "Traductions",
    },
  },

  panel: {
    apps: {
      crm: { name: "CRM", footerName: "CRM" },
      accounting: { name: "Comptabilité" },
      finance: { name: "Finance" },
      procurement: { name: "Approvisionnement" },
      inventory: { name: "Stocks" },
      purchase: { name: "Achats" },
      sales: { name: "Ventes" },
      manufacturing: { name: "Fabrication" },
      assets: { name: "Actifs" },
      hr: { name: "RH" },
      project: { name: "Projets" },
      timesheet: { name: "Feuille de temps" },
      documents: { name: "Documents" },
      pos: { name: "Point de vente" },
      rental: { name: "Location" },
      dashboard: { name: "Rapports" },
    },
    appsLabel: "Applications",
    settingsLabel: "Paramètres",
    coreLabel: "Gestion",
    backToHome: "Retour à l'accueil",
  },

  errors: {
    unknown: "Une erreur inconnue est survenue",
    network: "Impossible de se connecter au serveur. Vérifiez votre connexion.",
    timeout: "Délai d'attesa dépassé. Veuillez réessayer.",
    badRequest: "Requête invalide",
    unauthorized: "Session expirée. Veuillez vous reconnecter.",
    forbidden: "Vous n'avez pas la permission pour cette action.",
    notFound: "Ressource introuvable",
    tooManyRequests: "Trop de requêtes. Veuillez réessayer plus tard.",
    internalServerError: "Erreur serveur. Veuillez réessayer plus tard.",
    badGateway: "Serveur temporairement indisponible",
    serviceUnavailable: "Service temporairement indisponible",
    gatewayTimeout: "Le serveur ne répond pas",
    serverErrorWithStatus: "Erreur serveur ({status})",
  },

  // Dashboard
  dashboard: {
    title: "Tableau de bord",
    subtitle: "Résumé du système de gestion centralisé",
    welcome: "Bienvenue",
    guestName: "Invité",
    statusSummary: "Résumé de l'état actuel",
    currentTimeLabel: "Heure actuelle",
    todayLabel: "Aujourd'hui",
    activeCompanies: "Entreprises actives",
    activeOrganizations: "Organisations actives",
    activeUsers: "Utilisateurs actifs",
    activeRoles: "Rôles actifs",
    database: "Base de données",
    databaseUsed: "Espace utilisé",
    recentlyAddedUsers: "Utilisateurs ajoutés récemment",
    recentlyAddedCompanies: "Entreprises ajoutées récemment",
    noUsersYet: "Aucun utilisateur pour le moment",
    noCompaniesYet: "Aucune entreprise pour le moment",
    userPanel: "Panneau utilisateur",
    userPanelWelcome: "Bienvenue ! Accédez aux paramètres depuis le menu.",
    quickActions: "Actions rapides",
    systemHealthTitle: "Santé du système",
    systemLogsTitle: "Logs système",
    logChartTitle: "Distribution Erreurs/Avertissements",
    logErrorsLabel: "Erreurs",
    logWarningsLabel: "Avertissements",
    logSuccessLabel: "Succès",
    statusHealthy: "Sain",
    statusFailed: "Échec",
    statusUnknown: "Inconnu",
    lastRun: "Dernière exécution",
    failures24h: "Échecs 24h",
    noData: "Aucune donnée",
    systemComponents: {
      EMAIL_INGEST: "Courrier entrant (IMAP)",
      EMAIL_DISPATCH: "Courrier sortant (SMTP)",
      JOB_SCHEDULER: "Planificateur",
      API: "API",
    },
  },

  // Sidebar
  sidebar: {
    dashboard: "Tableau de bord",
    roles: "Rôles",
    users: "Utilisateurs",
    settings: "Paramètres",
    systemSettings: "Paramètres système",
    userSettings: "Paramètres utilisateur",
    organizations: "Organisations",
    logout: "Déconnexion",
    toggleMenu: "Basculer le menu",
    toggleMenuClose: "Fermer le menu",
    management: "Gestion",
    companies: "Entreprises",
    emailAccounts: "Comptes e-mail",
    dataSources: "Sources de données",
    translations: "Traductions",
  },

  // Settings
  settings: {
    title: "Paramètres",
    subtitle: "Gérer les paramètres entreprise et utilisateur",
    companySettingsSubtitle: "Paramètres généraux de l'entreprise",
    userSettingsSubtitle: "Vos paramètres personnels",
    securitySettingsSubtitle: "Gestion du mot de passe et sécurité",
    companySettings: "Paramètres Entreprise",
    userSettings: "Paramètres Utilisateur",
    general: "Général",
    personal: "Personnel",
    timezone: "Fuseau horaire",
    dateFormat: "Format de date",
    defaultCurrency: "Devise par défaut",
    defaultLanguage: "Langue par défaut",
    language: "Langue",
    companySettingsSaved: "Paramètres entreprise enregistrés",
    userSettingsSaved: "Paramètres utilisateur enregistrés",
    failedToLoad: "Échec du chargement des paramètres",
    saveFailed: "Échec de l'enregistrement des paramètres",
    profilePicture: "Photo de profil",
    maxFileSize: "Max 5Mo. Formats : JPEG, PNG, GIF, WebP",
    change: "Changer",
    uploading: "Téléchargement...",
    deleteAvatar: "Supprimer la photo",
    deleteAvatarConfirm: "Voulez-vous supprimer la photo de profil ?",
    avatarUploaded: "Photo de profil téléchargée",
    avatarDeleted: "Photo de profil supprimée",
    avatarUploadError: "Erreur lors du téléchargement",
    avatarDeleteError: "Erreur lors de la suppression",
    onlyImageFiles: "Seules les images sont acceptées",
    fileSizeError: "Le fichier doit être inférieur à 5Mo",
    appearance: "Apparence",
    theme: "Thème",
    light: "Clair",
    dark: "Sombre",
    pageSize: "Taille de page du tableau",
    defaultCompany: "Entreprise par défaut",
    selectDefaultCompany: "Sélectionnez l'entreprise par défaut",
    noCompaniesAssigned: "Aucune entreprise assignée",
    security: "Sécurité",
    changePassword: "Changer de mot de passe",
    currentPassword: "Mot de passe actuel",
    newPassword: "Nouveau mot de passe",
    confirmNewPassword: "Confirmer le nouveau mot de passe",
    passwordChanged: "Mot de passe changé avec succès",
    passwordChangeFailed: "Échec du changement de mot de passe",
    passwordMismatch: "Les mots de passe ne correspondent pas",
    passwordTooShort: "Le mot de passe doit contenir au moins 6 caractères",
    passwordSameAsOld: "Le nouveau mot de passe doit être différent de l'actuel",
    changingPassword: "Changement de mot de passe...",
    pleaseSelectCompany: "Veuillez sélectionner une entreprise",
  },

  // Organizations
  organizations: {
    title: "Organisations",
    code: "Code",
    name: "Nom de l'organisation",
    description: "Description",
    logo: "Logo",
    logoEmpty: "Aucun logo",
    uploadLogo: "Télécharger le logo",
    status: "Statut",
    smtp: "SMTP",
    imap: "IMAP",
    smtpEmailAccount: "Compte e-mail SMTP",
    imapEmailAccount: "Compte e-mail IMAP",
    active: "Active",
    inactive: "Inactive",
    newOrganization: "Nouvelle organisation",
    editOrganization: "Modifier l'organisation",
    deleteConfirm: "Voulez-vous supprimer cette organisation ?",
    deleteFailed: "Échec de la suppression de l'organisation",
    operationFailed: "L'opération a échoué",
    failedToLoad: "Échec du chargement des organisations",
    dataFormatError: "Format de données inattendu",
    searchPlaceholder: "Rechercher une organisation (nom, description)",
    empty: "Aucune donnée",
    notSet: "Non défini",
    noCreatePermission: "Pas de permission pour créer",
    noUpdatePermission: "Pas de permission pour modifier",
    noDeletePermission: "Pas de permission pour supprimer",
    company: "Nom de l'entreprise",
    statusDistribution: "Distribution des statuts d'organisations",
  },

  // Users
  users: {
    title: "Utilisateurs",
    newUser: "Nouvel utilisateur",
    editUser: "Modifier l'utilisateur",
    noUsersYet: "Aucun utilisateur pour le moment",
    failedToLoad: "Échec du chargement des utilisateurs",
    deleteConfirm: "Voulez-vous supprimer cet utilisateur ?",
    deleteFailed: "Échec de la suppression",
    operationFailed: "L'opération a échoué",
    passwordRequired: "Le mot de passe est obligatoire",
    passwordChangeHint: "(Remplir pour modifier)",
    fullName: "Nom complet",
    email: "E-mail",
    status: "Statut",
    roles: "Rôles",
    actions: "Actions",
    firstName: "Prénom *",
    lastName: "Nom *",
    password: "Mot de passe",
    noRoles: "Aucun rôle",
    rolesNotFound: "Aucun rôle trouvé",
    statusActive: "Actif",
    statusInactive: "Inactif",
    statusSuspended: "Suspendu",
    userInfoFailed: "Erreur de chargement des infos utilisateur",
    rolesFailed: "Erreur de chargement des rôles",
    copy: "Copier",
    isAdmin: "Admin",
    groups: "Groupes",
    groupCount: "Nombre de groupes",
    userGroupName: "Nom du groupe",
    userGroupCode: "Code du groupe",
    userGroupEmail: "E-mail du groupe",
    groupManager: "Responsable du groupe",
    members: "Membres",
    userGroupDescription: "Description",
    userGroupCreate: "Créer un nouveau groupe",
    userGroupEdit: "Modifier le groupe",
    manageMembers: "Gérer les membres",
    addMember: "Ajouter un membre",
    memberCount: "Nombre de membres",
    searchUsers: "Rechercher des utilisateurs...",
    noMembers: "Aucun membre pour le moment",
    selectUser: "Sélectionner un utilisateur",
    addUser: "Ajouter",
    removeUser: "Retirer",
    manageGroups: "Gérer les groupes",
    noUsersFound: "Aucun utilisateur trouvé",
    statusDistribution: "Distribution des statuts d'utilisateurs",
    groupStatusDistribution: "Distribution des statuts de groupes",
  },

  // Roles
  roles: {
    title: "Rôles",
    newRole: "Nouveau rôle",
    editRole: "Modifier le rôle",
    noRolesYet: "Aucun rôle pour le moment",
    failedToLoad: "Échec du chargement des rôles",
    deleteConfirm: "Voulez-vous supprimer ce rôle ?",
    deleteFailed: "Échec de la suppression",
    operationFailed: "L'opération a échoué",
    dataFormatError: "Erreur de format de données",
    permissionsFailed: "Erreur de chargement des permissions",
    name: "Nom du rôle *",
    description: "Description",
    permissions: "Permissions",
    permissionCount: "Nombre de permissions",
    type: "Type de rôle",
    typeSystem: "Système",
    typeCustom: "Personnalisé",
    typeAll: "Tous les rôles",
    searchPlaceholder: "Rechercher (nom, description, permission)...",
    systemBadge: "Système",
    permissionsCount: "Permissions ({count})",
    morePermissions: "+ {count} autres",
    copy: "Copier",
    copySuffix: " (copie)",
    clearFilters: "Effacer les filtres",
    noCreatePermission: "Pas de permission pour créer",
    noUpdatePermission: "Pas de permission pour modifier",
    noDeletePermission: "Pas de permission pour supprimer",
    systemRoleEditBlocked: "Les rôles système ne sont pas modifiables",
    systemRoleDeleteBlocked: "Les rôles système ne sont pas supprimables",
    noPermissions: "Aucune permission",
    paginationRange: "{start}-{end} sur {total}",
    paginationPage: "Page {page} / {total}",
    modules: {
      users: "Utilisateurs",
      groups: "Groupes",
      userGroupName: "Nom du groupe",
      userGroupCode: "Code du groupe",
      groupManager: "Responsable du groupe",
      members: "Membres",
      userGroupDescription: "Description",
      userGroupCreate: "Créer un nouveau groupe",
      userGroupEdit: "Modifier le groupe",
      roles: "Rôles",
      tenant: "Locataire",
      userSettingField: "Paramètre",
      settings: "Paramètres",
      organizations: "Organisations",
    },
    groups: "Groupes",
    userGroup: "Groupe",
    userGroupList: "Liste des groupes",
    userGroupCreate: "Créer un groupe",
    userGroupEdit: "Modifier le groupe",
    userGroupName: "Nom du groupe",
    userGroupDescription: "Description",
    userGroupCode: "Code",
    groupManager: "Responsable du groupe",
    members: "Membres",
    addMember: "Ajouter un membre",
    assignRole: "Assigner un rôle",
    assign: "Assigner",
    statusDistribution: "Distribution des statuts de rôles",
    assignRoleMatrix: "Matrice des rôles",
    roleMatrix: "Matrice des rôles",
    selectCompanyToViewMatrix: "Sélectionnez une entreprise pour voir la matrice",
    show: "Afficher",
    hide: "Masquer",
    searchPermissions: "Rechercher des permissions...",
    permission: "Permission",
    permissionsSelected: "{count} permissions sélectionnées",
  },

  // Companies
  companies: {
    title: "Entreprises",
    newCompany: "Nouvelle entreprise",
    editCompany: "Modifier l'entreprise",
    noCompaniesYet: "Aucune entreprise pour le moment",
    failedToLoad: "Échec du chargement des entreprises",
    deleteConfirm: "Voulez-vous supprimer cette entreprise ?",
    deleteFailed: "Échec de la suppression",
    operationFailed: "L'opération a échoué",
    dataFormatError: "Erreur de format de données",
    name: "Nom de l'entreprise *",
    status: "Statut",
    statusActive: "Active",
    statusInactive: "Inactive",
    searchPlaceholder: "Rechercher (nom, statut)...",
    clearFilters: "Effacer les filtres",
    copy: "Copier",
    copySuffix: " (copia)",
    noCreatePermission: "Pas de permission pour créer",
    noUpdatePermission: "Pas de permission pour modifier",
    noDeletePermission: "Pas de permission pour supprimer",
    paginationRange: "{start}-{end} sur {total}",
    paginationPage: "Page {page} / {total}",
    allStatuses: "Tous les statuts",
    statusDistribution: "Distribution des statuts d'entreprises",
    show: "Afficher",
    hide: "Masquer",
    logo: "Logo",
    logoEmpty: "Aucun logo",
    uploadLogo: "Télécharger le logo",
    smtpEmailAccount: "Compte SMTP",
    imapEmailAccount: "Compte IMAP",
    smtpEmailId: "Compte SMTP",
    imapEmailId: "Compte IMAP",
  },

  // Emails
  emails: {
    title: "Comptes E-mail",
    newEmail: "Nouveau compte e-mail",
    editEmail: "Modifier le compte e-mail",
    noEmailsYet: "Aucun compte pour le moment",
    failedToLoad: "Échec du chargement des comptes e-mail",
    deleteConfirm: "Supprimer ce compte ?",
    deleteConfirmMessage: "Voulez-vous supprimer le compte \"{name}\" ?",
    deleteFailed: "Échec de la suppression",
    operationFailed: "L'opération a échoué",
    dataFormatError: "Erreur de format de données",
    name: "Nom du compte",
    type: "Type",
    host: "Hôte",
    port: "Port",
    user: "Nom d'utilisateur",
    password: "Mot de passe",
    fromEmail: "E-mail expéditeur",
    secure: "Sécurisé (SSL/TLS)",
    secureConnection: "Connexion sécurisée (SSL/TLS)",
    status: "Statut",
    statusActive: "Actif",
    statusInactive: "Inactif",
    activate: "Activer",
    deactivate: "Désactiver",
    isActive: "Actif",
    searchPlaceholder: "Rechercher (nom, hôte, utilisateur)...",
    clearFilters: "Effacer les filtres",
    copy: "Copier",
    noCreatePermission: "Pas de permission pour créer",
    noUpdatePermission: "Pas de permission pour modifier",
    noDeletePermission: "Pas de permission pour supprimer",
    paginationRange: "{start}-{end} sur {total}",
    paginationPage: "Page {page} / {total}",
    allStatuses: "Tous les statuts",
    allTypes: "Tous les types",
    statusDistribution: "Distribution des statuts de comptes",
    show: "Afficher",
    hide: "Masquer",
    nameRequired: "Le nom du compte est obligatoire",
    hostRequired: "L'hôte est obligatoire",
    portRequired: "Le port est obligatoire",
    userRequired: "Le nom d'utilisateur est obligatoire",
    passwordRequired: "Le mot de passe est obligatoire",
    leaveBlankToKeep: "Laisser vide pour conserver l'actuel",
    noData: "Aucune donnée",
    noEmailsMessage: "Aucun compte trouvé",
    passwordEditHint: "Laisser vide pour ne pas modifier",
    noSearchResults: "Aucun résultat",
    namePlaceholder: "ex. Support ou info@entreprise.com",
    deleteConfirmTitle: "Supprimer le compte",
  },

  // Login
  login: {
    title: "QubePro ERP",
    subtitle: "Planification des ressources d'entreprise",
    heroTitle: "Gestion d'entreprise en un seul point",
    heroSubtitle: "QubePro ERP unifie la gestion des entreprises, organisations, utilisateurs et rôles sur une plateforme sécurisée.",
    languageToggleLabel: "Langue",
    turkish: "Turc",
    english: "Anglais",
    email: "E-mail",
    password: "Mot de passe",
    emailPlaceholder: "exemple@domaine.com",
    emailHint: "Le locataire est déterminé automatiquement par le domaine",
    passwordPlaceholder: "••••••••",
    loginButton: "Connexion",
    loggingIn: "Connexion...",
    loginFailed: "Échec de la connexion",
    darkMode: "Mode sombre",
    lightMode: "Mode clair",
    loginPageTitle: "Accès",
    submitButton: "Connexion",
    featureTitle: "Système de gestion centralisé",
    featureDescription: "Gérez vos relations clients, ventes, stocks et processus financiers dans une plateforme intégrée.",
    featureCard1Title: "Gestion des clients",
    featureCard1Description: "Suivez vos clients, ventes potentielles et relations commerciales depuis un emplacement central.",
    featureCard2Title: "Ventes et commandes",
    featureCard2Description: "Gérez facilement les devis, commandes et processus de vente pour augmenter vos revenus.",
    featureCard3Title: "Rapports et analyses",
    featureCard3Description: "Surveillez les performances avec des rapports en temps réel et prenez des décisions basées sur les données.",
    featureCard4Title: "Intégration et paramètres",
    featureCard4Description: "Personnalisez votre système, intégrez d'autres outils et automatisez vos processus métier.",
    pleaseWait: "Veuillez patienter",
    serverConfiguring: "Configuration du serveur en cours, veuillez patienter...",
    dbCreating: "Création de la base de données et des configurations. Cela peut prendre quelques secondes.",
    emailLabel: "E-mail",
    passwordLabel: "Mot de passe",
    emailPlaceholderFull: "Veuillez entrer votre e-mail",
    passwordPlaceholderFull: "Veuillez entrer votre mot de passe",
    showPassword: "Voir",
    hidePassword: "Cacher",
    emailRequired: "L'e-mail est obligatoire",
    passwordRequired: "Le mot de passe est obligatoire",
    backendUnavailable: "Serveur indisponible. Réessayez plus tard.",
    backendConnectFailed: "Échec de connexion au serveur. Réessayez plus tard.",
    backendConnectFailedDev: "Échec de connexion. Vérifiez que le backend tourne ({apiUrl}).",
    fieldEmail: "E-mail",
    fieldPassword: "Mot de passe",
    errors: {
      invalidCredentials: "E-mail ou mot de passe incorrect.",
      databaseUnavailable: "Base de données indisponible ou configuration requise. Reconnectez-vous.",
      accountInactive: "Compte inactif. Veuillez contacter l'administrateur.",
      sessionExpired: "Session expirée. Veuillez vous reconnecter.",
      tooManyAttempts: "Trop de tentatives. Veuillez réessayer plus tard.",
      sessionTooLarge: "Données de session trop lourdes. Veuillez réessayer.",
    },
  },

  // DataSources
  dataSources: {
    title: "Sources de Données",
    subtitle: "Gérer les connexions aux bases de données clients",
    newDataSource: "Nouvelle source de données",
    editDataSource: "Modifier la source de données",
    noDataSourcesYet: "Aucune source pour le moment",
    failedToLoad: "Échec du chargement des sources",
    deleteConfirm: "Voulez-vous supprimer cette source de données ?",
    deleteFailed: "Échec de la suppression",
    operationFailed: "L'opération a échoué",
    dataFormatError: "Erreur de format de données",
    name: "Nom affiché",
    driver: "Pilote",
    server: "Serveur",
    port: "Port",
    databaseName: "Nom de la BD",
    username: "Utilisateur",
    password: "Mot de passe",
    schema: "Schéma",
    sourceType: "Type de source",
    source: "Source",
    target: "Cible",
    sourceSystem: "Système source",
    targetSystem: "Système cible",
    status: "Statut",
    statusActive: "Active",
    statusInactive: "Inactive",
    searchPlaceholder: "Rechercher (nom, serveur, BD)...",
    clearFilters: "Effacer les filtres",
    copy: "Copier",
    paginationRange: "{start}-{end} / {total}",
    paginationPage: "Page {page} / {total}",
    allStatuses: "Tous les statuts",
    allTypes: "Tous",
    statusDistribution: "Distribution des statuts de sources",
    show: "Afficher",
    hide: "Masquer",
    nameRequired: "Le nom est obligatoire",
    serverRequired: "L'adresse du serveur est obligatoire",
    databaseRequired: "Le nom de la BD est obligatoire",
    passwordRequired: "Le mot de passe est obligatoire",
    testConnection: "Tester la connexion",
    testing: "Test en cours...",
    testSuccess: "Connexion réussie !",
    testFailed: "Échec de connexion : ",
    integratedSecurity: "Sécurité intégrée",
    ssl: "SSL",
    connectionTimeout: "Délai de connexion (sec)",
    description: "Description",
    noCreatePermission: "Pas de permission pour créer",
    noUpdatePermission: "Pas de permission pour modifier",
    noDeletePermission: "Pas de permission pour supprimer",
  },

  aiProviders: {
    title: "Fournisseurs d'IA",
    subtitle: "Gérer les modèles d'IA et intégrations",
    newProvider: "Nouveau fournisseur d'IA",
    editProvider: "Modifier le fournisseur d'IA",
    noProvidersYet: "Aucune donnée",
    failedToLoad: "Échec du chargement des fournisseurs d'IA",
    deleteConfirm: "Êtes-vous sûr de vouloir supprimer ce fournisseur d'IA ?",
    deleteFailed: "Échec de la suppression du fournisseur d'IA",
    operationFailed: "L'opération a échoué",
    name: "Nom du fournisseur *",
    provider: "Type de fournisseur",
    baseUrl: "URL de base",
    apiKey: "Clé API",
    description: "Description",
    status: "Statut",
    statusActive: "Actif",
    statusInactive: "Inactif",
    searchPlaceholder: "Rechercher (nom, type, URL)...",
    clearFilters: "Effacer les filtres",
    noCreatePermission: "Pas de permission pour créer un fournisseur d'IA",
    statusDistribution: "Distribution des statuts de fournisseurs d'IA",
    baseUrlPlaceholder: "ex. http://localhost:11434, https://api.custom.ai/v1",
    baseUrlEmptyDefault: "Laisser vide pour les valeurs par défaut",
    apiKeyPlaceholder: "Entrer la clé API",
  },
};
